/* eslint-disable no-undefined */
import React from "react";
import { format, lastDayOfMonth, startOfMonth, addDays } from "date-fns";
//Limitless imports
import { useTranslation, useAppConfig } from "@limitless/common/hooks";
import { useExpertStats } from "../expert-stats/ExpertStatsHook";
import { metrics } from "../../../portal";
import { useExpertProfileContext } from "../expert-profile/ExpertProfileContext";
import { useExpertPendingQuestionsContext } from "../expert-pending-questions/ExpertPendingQuestionsContext";
const LAST_RATING_MONTH = "2023/04/01";
export const useExpertDashboard = () => {
  const {
    t
  } = useTranslation();
  const {
    profile,
    getFormattedBalance
  } = useExpertProfileContext();
  const {
    dashboardTrendMetrics,
    groupSuggestions,
    getDashboardStats,
    currentMonthState,
    previousMonthState
  } = useExpertStats(false, t);
  const {
    pendingQuestions
  } = useExpertPendingQuestionsContext();
  const {
    getMinWithdrawalAmount
  } = useAppConfig();
  const [feesEarned, setFeesEarned] = React.useState(0);
  const [currentMonthTotal, setCurrentMonthTotal] = React.useState(0);
  const [previousMonthTotal, setPreviousMonthTotal] = React.useState(0);
  const [expertState, setExpertState] = React.useState(metrics[0].value);
  const [month, setMonth] = React.useState({
    label: "This Month",
    value: "this month"
  });
  const [group, setGroup] = React.useState(groupSuggestions[0]);
  const [categories, setCategories] = React.useState([]);
  const paymentValueThreshold = React.useMemo(() => {
    return getMinWithdrawalAmount(profile.balanceCurrency);
  }, [profile]);
  const getTotalFromStat = stat => {
    if (!stat) {
      return "";
    }
    let total = 0;
    stat.slice(-7).map(val => {
      total += val.value;
    });
    return total;
  };
  const getDates = criteria => {
    const date = format(new Date(criteria.value), "YYYY-MM-DD");
    const getDate = isNaN(Date.parse(date)) ? format(startOfMonth(new Date()), "YYYY-MM-DD") : date;
    const currentMonthDate = criteria.label === "This Month" ? format(new Date(), "YYYY-MM-DD") : format(lastDayOfMonth(getDate), "YYYY-MM-DD");
    const currentMonthLastDate = getDate;
    return [currentMonthLastDate, currentMonthDate];
  };
  const getPreviousDate = criteria => {
    const date = isNaN(Date.parse(criteria)) ? new Date() : new Date(criteria.replaceAll("-", "/"));
    const previousDate = format(new Date(date.getFullYear(), date.getMonth() - 1), "YYYY-MM-DD");
    const previousMonthStartDate = format(lastDayOfMonth(previousDate), "YYYY-MM-DD");
    const previousMonthEndDate = previousDate;
    return [previousMonthEndDate, previousMonthStartDate];
  };

  // To get the currrent month and previous month fees earned data
  const fetchMontlyData = (criteria, group) => {
    setMonth(criteria);
    const date = getDates(criteria);
    const previousDate = getPreviousDate(date[0]);
    setExpertState(metrics[0].value);
    getDashboardStats(date[0], date[1], group, [metrics[0].value], false, true);
    getDashboardStats(previousDate[0], previousDate[1], group, [metrics[0].value], false, false);
  };
  const fetchDashboardStats = () => {
    const endDate = format(addDays(new Date(), -14), "YYYY-MM-DD");
    const startDate = format(new Date(), "YYYY-MM-DD");
    getDashboardStats(endDate, startDate, {
      label: t("All Groups"),
      value: "all"
    }, [metrics[1].value, metrics[2].value, metrics[3].value], true, false);
  };

  // To get the currrent month and previous month rating data
  const fetchMontlyRatingData = (criteria, group) => {
    setMonth(criteria);
    setGroup(group);
    const date = getDates(criteria);
    const previousDate = getPreviousDate(date[0]);
    setExpertState(metrics[5].value);
    getDashboardStats(date[0], date[1], group, [metrics[5].value], false, true);
    getDashboardStats(previousDate[0], previousDate[1], group, [metrics[5].value], false, false);
  };
  const fetchMontlyTasks = (criteria, group) => {
    setMonth(criteria);
    const date = getDates(criteria);
    const previousDate = getPreviousDate(date[0]);
    setExpertState(metrics[1].value);
    getDashboardStats(date[0], date[1], group, [metrics[1].value, metrics[2].value, metrics[3].value], false, true);
    getDashboardStats(previousDate[0], previousDate[1], group, [metrics[1].value, metrics[2].value, metrics[3].value], false, false);
  };
  const getDayOfMonthSuffix = day => {
    if (day >= 11 && day <= 13) {
      return "th";
    }
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };
  const getFormattedDate = formatedate => {
    const date = new Date(formatedate.replaceAll("-", "/"));
    const month = date.toLocaleString("en-US", {
      month: "long"
    });
    const day = `${date.getDate()}${getDayOfMonthSuffix(date.getDate())}`;
    return `${day} ${month} ${date.getFullYear()}`;
  };
  const pendingFeesCount = React.useMemo(() => {
    let sum = 0;
    pendingQuestions === null || pendingQuestions === void 0 ? void 0 : pendingQuestions.filtered.map(data => {
      const getMaxFees = data.fee && data.fee.split("-");
      if (getMaxFees && getMaxFees.length === 1) {
        sum += +getMaxFees[0].slice(1);
      } else if (getMaxFees && getMaxFees.length === 2) {
        sum += +getMaxFees[1].slice(2);
      }
    });
    return sum;
  }, [pendingQuestions]);
  const monthSuggestions = React.useMemo(() => {
    const start = new Date(profile.created).getMonth() + 1;
    const end = new Date().getMonth() + 1;
    const startYear = new Date(profile.created).getFullYear();
    const endYear = new Date().getFullYear();
    const dates = [];
    for (let i = startYear; i <= endYear; i++) {
      const endMonth = i !== endYear ? 11 : end - 1;
      const startMonth = i === startYear ? start - 1 : 0;
      for (let j = startMonth; j <= endMonth; j = j > 12 ? j % 12 || 11 : j + 1) {
        const month = j + 1;
        const displayMonth = month < 10 ? month : month;
        const date = new Date();
        date.setDate(1);
        date.setMonth(displayMonth - 1);
        dates.push({
          key: `${Math.random()}`,
          label: displayMonth === new Date().getMonth() + 1 && endYear === i ? "This Month" : [date.toLocaleString("en-US", {
            month: "long"
          }), i].join(" "),
          value: [i, date.toLocaleString("en-US", {
            month: "2-digit"
          }), "01"].join("/")
        });
      }
    }
    const date = dates.reverse();
    setMonth(date[0]);
    return date;
  }, [profile.created]);
  const ratingMonths = React.useMemo(() => {
    return monthSuggestions.filter(month => month.value >= LAST_RATING_MONTH);
  }, [monthSuggestions]);
  const getRowData = monthMetrics => {
    const category = [];
    const row = [];
    let total = 0;
    for (let j = 0; j < (monthMetrics === null || monthMetrics === void 0 ? void 0 : monthMetrics.length); j++) {
      total += monthMetrics[j].value;
      category.push(`${j + 1}${getDayOfMonthSuffix(j + 1)}`);
      const rowData = {
        x: `${j + 1}${getDayOfMonthSuffix(j + 1)}`,
        y: monthMetrics[j].value,
        value: monthMetrics[j].value,
        date: getFormattedDate(monthMetrics[j].date)
      };
      row.push(rowData);
    }
    return [row, total, category];
  };
  const fetchMonthlyRewardStatsPlotting = currentMonthState => {
    const data = [];
    let rewardsPoint = 0;
    const category = [];
    const plotMetrics = currentMonthState === null || currentMonthState === void 0 ? void 0 : currentMonthState.result;
    for (let j = 0; j < (plotMetrics === null || plotMetrics === void 0 ? void 0 : plotMetrics.length); j++) {
      var _plotMetrics$j;
      const currentMonthRewards = +((_plotMetrics$j = plotMetrics[j]) === null || _plotMetrics$j === void 0 ? void 0 : _plotMetrics$j.value);
      rewardsPoint += currentMonthRewards;
      category.push(`${j + 1}${getDayOfMonthSuffix(j + 1)}`);
      const rowData = {
        x: `${j + 1}${getDayOfMonthSuffix(j + 1)}`,
        y: rewardsPoint,
        value: getFormattedBalance(plotMetrics[j].value),
        date: getFormattedDate(plotMetrics[j].date)
      };
      data.push(rowData);
    }
    const dayDifference = 31 - data.length;
    const initalDate = data.length;
    for (let i = 1; i <= dayDifference; i++) {
      category.push(`${initalDate + i}${getDayOfMonthSuffix(initalDate + i)}`);
      data.push({
        x: `${initalDate + i}${getDayOfMonthSuffix(initalDate + i)}`,
        y: undefined,
        value: undefined,
        date: undefined
      });
    }
    return [data, category, rewardsPoint];
  };
  const fetchMonthlyStatsTaskPlotting = currentMonthState => {
    const totalTask = 0;
    let data = {};
    const plotMetricsrespond = currentMonthState === null || currentMonthState === void 0 ? void 0 : currentMonthState.responded;
    const plotMetricsreview = currentMonthState === null || currentMonthState === void 0 ? void 0 : currentMonthState.reviewed;
    const plotMetricsescalate = currentMonthState === null || currentMonthState === void 0 ? void 0 : currentMonthState.escalate;
    const respond = getRowData(plotMetricsrespond, totalTask);
    const escalate = getRowData(plotMetricsescalate, respond[1]);
    const review = getRowData(plotMetricsreview, escalate[1]);
    data = {
      respond: respond[0],
      escalate: escalate[0],
      review: review[0]
    };
    const total = respond[1] + review[1] + escalate[1];
    return review && [data, review[2], total];
  };
  const fetchMonthlyRatingStatsPlotting = currentMonthState => {
    const category = [];
    const data = [];
    let ratingScore = 0;
    const plotMetrics = currentMonthState === null || currentMonthState === void 0 ? void 0 : currentMonthState.result;
    for (let j = 0; j < (plotMetrics === null || plotMetrics === void 0 ? void 0 : plotMetrics.length); j++) {
      const currentMonthRating = +plotMetrics[j].value;
      ratingScore = currentMonthRating === 0 ? +ratingScore : +plotMetrics[j].value;
      category.push(`${j + 1}${getDayOfMonthSuffix(j + 1)}`);
      const rowData = {
        x: `${j + 1}${getDayOfMonthSuffix(j + 1)}`,
        y: plotMetrics[j].value === 0 ? ratingScore : plotMetrics[j].value,
        value: `${plotMetrics[j].value}%`,
        date: getFormattedDate(plotMetrics[j].date)
      };
      data.push(rowData);
    }
    const dayDifference = 31 - data.length;
    const initalDate = data.length;
    for (let i = 1; i <= dayDifference; i++) {
      category.push(`${initalDate + i}${getDayOfMonthSuffix(initalDate + i)}`);
      data.push({
        x: `${initalDate + i}${getDayOfMonthSuffix(initalDate + i)}`,
        y: undefined,
        value: "0%",
        date: undefined
      });
    }
    return [data, category, ratingScore];
  };
  const getMonthPlotting = currentMonthState => {
    return currentMonthState && expertState === "expert.custResp.rating" ? fetchMonthlyRatingStatsPlotting(currentMonthState) : expertState === "expert.resp" ? fetchMonthlyStatsTaskPlotting(currentMonthState) : fetchMonthlyRewardStatsPlotting(currentMonthState);
  };
  const currentMonthPloting = React.useMemo(() => {
    const currentMonthPlotting = getMonthPlotting(currentMonthState);
    setFeesEarned(currentMonthPlotting[2]);
    setCurrentMonthTotal(currentMonthPlotting[2]);
    setCategories(currentMonthPlotting[1]);
    return currentMonthPlotting[0];
  }, [currentMonthState]);
  const previousMonthPloting = React.useMemo(() => {
    const previousMonthPlotting = getMonthPlotting(previousMonthState);
    setPreviousMonthTotal(previousMonthPlotting[2]);
    return previousMonthPlotting[0];
  }, [previousMonthState]);

  //to get the sum of 7 days before all task types
  const getTotalFromLastSevenDayStat = stat => {
    if (!stat) {
      return "";
    }
    let total = 0;
    stat.slice(-14, -7).map(val => {
      total += val.value;
    });
    return total;
  };
  const taskCompleted = React.useMemo(() => {
    return getTotalFromStat(dashboardTrendMetrics.responded) + getTotalFromStat(dashboardTrendMetrics.escalate) + getTotalFromStat(dashboardTrendMetrics.reviewed);
  }, [dashboardTrendMetrics]);
  const taskTrendingBefore = React.useMemo(() => {
    return getTotalFromLastSevenDayStat(dashboardTrendMetrics.responded) + getTotalFromLastSevenDayStat(dashboardTrendMetrics.escalate) + getTotalFromLastSevenDayStat(dashboardTrendMetrics.reviewed);
  }, [dashboardTrendMetrics]);
  const countPercentage = (currentMonth, previousMonth) => {
    var _monthSuggestions;
    return ((_monthSuggestions = monthSuggestions[monthSuggestions.length - 1]) === null || _monthSuggestions === void 0 ? void 0 : _monthSuggestions.label) !== (month === null || month === void 0 ? void 0 : month.label) && (previousMonth === 0 && currentMonth !== previousMonth ? 100 : Math.abs(100 - currentMonth * 100 / previousMonth));
  };
  const trendingStatus = taskTrendingBefore > taskCompleted ? false : true;
  const trendingRate = React.useMemo(() => {
    const rate = taskTrendingBefore === 0 && taskCompleted !== taskTrendingBefore ? 100 : Math.abs(100 - taskCompleted * 100 / taskTrendingBefore);
    return monthSuggestions.length === 1 ? new Date().getDate() > 13 && rate : rate;
  }, [taskCompleted, taskTrendingBefore]);
  const status = previousMonthTotal > currentMonthTotal ? false : true;
  const feeEarnedRate = countPercentage(currentMonthTotal, previousMonthTotal);
  const rating = React.useMemo(() => {
    return (month === null || month === void 0 ? void 0 : month.label) === "This Month" && group.value === "all" ? profile === null || profile === void 0 ? void 0 : profile.custRating : Math.round(feesEarned);
  }, [group, month, feesEarned, profile]);
  const ratingStatus = React.useMemo(() => {
    return Math.round(previousMonthTotal) > rating ? false : true;
  }, [categories, previousMonthTotal, currentMonthTotal, rating]);
  const ratingRate = React.useMemo(() => {
    return countPercentage(rating, Math.round(previousMonthTotal));
  }, [categories, previousMonthTotal, currentMonthTotal, rating]);
  const taskTrendRate = React.useMemo(() => {
    return countPercentage(currentMonthTotal, previousMonthTotal);
  }, [categories, previousMonthTotal, currentMonthTotal]);
  return {
    feesEarned,
    currentMonthPloting,
    previousMonthPloting,
    monthSuggestions,
    ratingMonths,
    categories,
    fetchMontlyData,
    fetchDashboardStats,
    fetchMontlyRatingData,
    fetchMontlyTasks,
    month,
    setMonth,
    group,
    setGroup,
    taskCompleted,
    trendingStatus,
    trendingRate,
    status,
    rating,
    feeEarnedRate,
    paymentValueThreshold,
    pendingFeesCount,
    ratingRate,
    ratingStatus,
    taskTrendRate
  };
};